import {type FishGroup} from 'client';
import {useState, type Dispatch, type SetStateAction} from 'react';
import {useTranslation} from 'react-i18next';
import {IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography} from '@mui/material';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import UndoIcon from '@mui/icons-material/Undo';
import {CVIcon} from 'styles/icons';
import NumberFormatCustom from 'helpers/NumberFormatter';
import {actionTypeEnum, type IFishGroup, type IFishGroupAction} from './helpers/reducer';

export const CVField = ({state, dispatch}: {state: IFishGroup; dispatch: Dispatch<IFishGroupAction>}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {t} = useTranslation('common');
  const isDefault = !state?.cvAttribute?.isPinned && !state?.cvAttribute?.value;
  const isMixed = mixedCV(state.fishGroups ?? []) && !state.cvEdited;
  return (
    <Stack direction="row">
      <CVMEnu {...{anchorEl, setAnchorEl, dispatch}} />
      <Stack direction={'row'} width={90} alignItems={'center'} spacing={1}>
        <Typography variant="body2" color={'secondary'}>
          {t('cvAbbreviation')}
        </Typography>
        {!isMixed && state.cvAttribute?.isPinned && (
          <Tooltip title={t('isEdited')}>
            <DriveFileRenameOutlineRoundedIcon fontSize={'inherit'} color="primary" />
          </Tooltip>
        )}
      </Stack>

      <Stack direction={'row'} alignItems={'center'} justifyContent={'left'} spacing={1}>
        <CVIcon fontSize="inherit" />
        {isDefault || isMixed ? (
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1}
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
            sx={{cursor: 'pointer'}}
            data-testid="cv-model"
          >
            <Typography variant="body2">{isMixed ? t('mixed') : t('default')}</Typography>
          </Stack>
        ) : (
          <TextField
            data-testid="fish-group-cv"
            sx={{'&:hover .undo-button': {opacity: 1}}}
            value={state.cvAttribute?.value ?? ''}
            size="small"
            variant={'outlined'}
            InputProps={{
              inputComponent: NumberFormatCustom as (props) => JSX.Element,
              endAdornment: state?.cvAttribute?.isPinned && (
                <Tooltip title={t('removeOverride')} placement="bottom-end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={(event) => {
                      dispatch({type: actionTypeEnum.resetCV});
                      event.stopPropagation();
                    }}
                    className="undo-button"
                    sx={{opacity: 0}}
                  >
                    <UndoIcon color="secondary" fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              ),
            }}
            onChange={(event) => {
              if (event.target.value !== '') {
                const value = parseFloat(event.target.value);
                dispatch({type: actionTypeEnum.setCustomCVValue, CVValue: value});
              }
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};

const CVMEnu = ({
  anchorEl,
  setAnchorEl,
  dispatch,
}: {
  anchorEl: HTMLElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
  dispatch: Dispatch<IFishGroupAction>;
}) => {
  const open = Boolean(anchorEl);
  const {t} = useTranslation('common');
  const handleSetCustomModel = () => {
    dispatch({type: actionTypeEnum.setCustomCVValue});
    setAnchorEl(null);
  };
  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        setAnchorEl(null);
      }}
    >
      <MenuItem onClick={handleSetCustomModel} data-testid="set-custom-cv">
        <Typography>{t('setCustomCV')}</Typography>
      </MenuItem>
    </Menu>
  );
};

const mixedCV = (fishGroups: FishGroup[]): boolean => {
  if (fishGroups.length > 1) {
    const firstValue = fishGroups[0].cvAttribute?.value;
    return fishGroups.some((fishGroup) => fishGroup.cvAttribute?.value !== firstValue);
  }
  return false;
};
